/**
 * Spaced Repetition Service
 * Single Responsibility: Scheduling line reviews (simplified SM-2), stored per browser
 */
const STORAGE_KEY = 'esekySpacedRepetition';
const CHANGE_EVENT = 'eseky-srs-change';
const DAY = 24 * 60 * 60 * 1000;
const RETRY_DELAY = 10 * 60 * 1000; // a failed line comes back after 10 minutes
const PERFECTED_REPS = 3; // consecutive successful reviews to consider a line perfected

const load = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch (error) {
    return {};
  }
};

const save = (cards) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cards));
  } catch (error) {
    console.error('Failed to save spaced repetition progress:', error);
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
};

const newCard = (now) => ({
  learnedAt: now,
  due: now,
  interval: 0,
  ease: 2.5,
  reps: 0,
  lapses: 0,
  lastReviewedAt: null,
  lastResult: null
});

export const getCard = (lineId) => load()[lineId] || null;

export const getCards = () => load();

/**
 * Status of a line: 'new' (never learned), 'due' (to review now),
 * 'learning' (scheduled later) or 'perfected'
 */
export const getStatus = (card, now = Date.now()) => {
  if (!card) return 'new';
  if (card.due <= now) return 'due';
  if (card.reps >= PERFECTED_REPS) return 'perfected';
  return 'learning';
};

export const getLineStatus = (lineId, cards = load(), now = Date.now()) => getStatus(cards[lineId], now);

/** Marks a line as learned (discovered in Learn mode). It is immediately available for practice. */
export const markLearned = (lineId) => {
  const cards = load();
  if (!cards[lineId]) {
    cards[lineId] = newCard(Date.now());
    save(cards);
  }
  return cards[lineId];
};

/** Records a practice result and schedules the next review */
export const recordReview = (lineId, success) => {
  const now = Date.now();
  const cards = load();
  const card = { ...(cards[lineId] || newCard(now)) };

  if (success) {
    card.reps += 1;
    if (card.reps === 1) {
      card.interval = 1;
    } else if (card.reps === 2) {
      card.interval = 3;
    } else {
      card.interval = Math.round(card.interval * card.ease);
    }
    card.ease = Math.min(3, card.ease + 0.1);
    card.due = now + card.interval * DAY;
  } else {
    card.lapses += 1;
    card.reps = 0;
    card.interval = 0;
    card.ease = Math.max(1.3, card.ease - 0.2);
    card.due = now + RETRY_DELAY;
  }

  card.lastReviewedAt = now;
  card.lastResult = success ? 'success' : 'failure';
  cards[lineId] = card;
  save(cards);
  return card;
};

/** Progress summary for a list of lines */
export const getProgress = (lines = [], cards = load(), now = Date.now()) => {
  const summary = { total: lines.length, learned: 0, due: 0, perfected: 0, nextDue: null };
  lines.forEach(line => {
    const card = cards[line.id];
    if (!card) return;
    summary.learned += 1;
    const status = getStatus(card, now);
    if (status === 'due') summary.due += 1;
    if (status === 'perfected') summary.perfected += 1;
    if (status !== 'due' && (summary.nextDue === null || card.due < summary.nextDue)) {
      summary.nextDue = card.due;
    }
  });
  return summary;
};

/** Human readable delay until a timestamp, e.g. "in 3 days" */
export const formatDue = (timestamp, now = Date.now()) => {
  const diff = timestamp - now;
  if (diff <= 0) return 'now';
  const minutes = Math.round(diff / 60000);
  if (minutes < 60) return `in ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `in ${hours} h`;
  const days = Math.round(hours / 24);
  return `in ${days} day${days > 1 ? 's' : ''}`;
};

/** Subscribe to progress changes (this tab and other tabs) */
export const subscribe = (callback) => {
  const onStorage = (event) => {
    if (event.key === STORAGE_KEY) callback();
  };
  window.addEventListener(CHANGE_EVENT, callback);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, callback);
    window.removeEventListener('storage', onStorage);
  };
};
